import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  emptyUnverifiedCategories,
  nextActions,
  phoneCategories,
  phoneNotPickedUpProblems,
  phonePickedUpProblems,
  photoCategories,
  photoFrontProblems,
  photoInsideProblems,
  typeCategories,
} from '../constants/categoryUnverified';
import { colors, spacing } from '../constants/theme';
import { Button, Text, TextInput } from '../core-ui';
import { RowContainer } from '../core-ui/RowContainer';
import { UnverifiedCategory } from '../scenes/workInProgress/types';

import { Dropdown } from './dropdowns';

export function UnverifiedDropDownSegment({
  data,
  viewMode,
}: {
  data: Array<UnverifiedCategory>;
  viewMode: boolean;
}) {
  const [unverifiedCategories, setUnverifiedCategories] = useState(data);

  let getProblemFromCategories = (category: string) => {
    switch (category) {
      case 'Foto Bagian Dalam':
        return photoInsideProblems;
      case 'Foto Tampak Depan':
        return photoFrontProblems;
      case 'Telepon diangkat':
        return phonePickedUpProblems;
      case 'Telepon tidak diangkat':
        return phoneNotPickedUpProblems;
      default:
        return [];
    }
  };

  let onChangeUnverifiedCategories = (
    value: string,
    field: keyof UnverifiedCategory,
    index: number,
  ) => {
    const newUnverifCategories = unverifiedCategories.map(
      (unverifiedCategory, idx) => {
        if (idx === index) {
          if (field === 'type') {
            return {
              ...emptyUnverifiedCategories,
              type: value,
            };
          }

          if (field === 'problem' && value !== 'Lainnya') {
            return {
              ...unverifiedCategory,
              problem: value,
              action:
                nextActions.find((item) => item.problem === value)?.action ||
                '',
            };
          }

          return {
            ...unverifiedCategory,
            [field]: value,
          };
        }

        return unverifiedCategory;
      },
    );

    setUnverifiedCategories(newUnverifCategories);
  };

  let deleteUnverifiedCategories = (index: number) => {
    const newUnverifCategories = unverifiedCategories.filter(
      (_, idx) => idx !== index,
    );
    setUnverifiedCategories(newUnverifCategories);
  };

  return (
    <>
      {unverifiedCategories.map(
        ({ type, detail, problem, action, customProblem }, idx) => (
          <View key={idx}>
            <RowContainer style={styles.unverifCategoryContainer}>
              <Dropdown
                containerStyle={[styles.w20, styles.mRightSpacing]}
                label="Tipe Kategori"
                value={type}
                onChange={(value: string) =>
                  onChangeUnverifiedCategories(value, 'type', idx)
                }
                options={typeCategories}
                disabled={viewMode}
                required
              />
              <Dropdown
                containerStyle={[styles.w20, styles.mRightSpacing]}
                label="Detail Kategori"
                value={detail}
                onChange={(value: string) =>
                  onChangeUnverifiedCategories(value, 'detail', idx)
                }
                options={type === 'Telepon' ? phoneCategories : photoCategories}
                disabled={viewMode || !type}
                required
              />
              <Dropdown
                containerStyle={[styles.w40, styles.mRightSpacing]}
                label="Permasalahan Ditemukan"
                value={problem}
                onChange={(value: string) =>
                  onChangeUnverifiedCategories(value, 'problem', idx)
                }
                options={getProblemFromCategories(detail)}
                disabled={viewMode || !detail}
                required
              />
              {idx > 0 ? (
                <Button
                  large
                  preset="secondary"
                  title="-"
                  style={styles.w10}
                  onPress={() => deleteUnverifiedCategories(idx)}
                  disabled={viewMode || !detail}
                />
              ) : null}
            </RowContainer>

            {problem === 'Lainnya' ? (
              <>
                <TextInput
                  containerStyle={styles.xxsBottomSpacing}
                  label="Lainnya"
                  value={customProblem}
                  onChangeText={(value: string) =>
                    onChangeUnverifiedCategories(value, 'customProblem', idx)
                  }
                  placeholder="Keterangan"
                  disabled={viewMode}
                  required
                />
                <TextInput
                  containerStyle={styles.mBottomSpacing}
                  label="Next Action"
                  value={action}
                  onChangeText={(value: string) =>
                    onChangeUnverifiedCategories(value, 'action', idx)
                  }
                  placeholder="Masukan Aksi Berikutnya"
                  disabled={viewMode}
                  required
                />
              </>
            ) : (
              problem.length > 0 && (
                <Text style={styles.mBottomSpacing}>
                  {`Next Action: ${action}`}
                </Text>
              )
            )}
            <View style={[styles.border, styles.mBottomSpacing]} />
          </View>
        ),
      )}
    </>
  );
}

const styles = StyleSheet.create({
  root: { width: 529, height: 1000, backgroundColor: colors.background.white },
  row: { flexDirection: 'row' },
  flex: { flex: 1 },
  w100: { width: '100%' },
  w50: { width: '48%' },
  w40: { width: '40%' },
  w30: { width: '30%' },
  w20: { width: '20%' },
  w15: { width: '15%' },
  w10: { width: '10%' },
  border: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.button.border.primary,
  },
  ///////////////////////////////////////////////
  header: {
    backgroundColor: colors.background.white,
    flexDirection: 'row',
    height: 40,
    shadowColor: colors.background.shadow,
  },
  tab: {
    justifyContent: 'flex-start',
    height: '100%',
    marginHorizontal: spacing.l,
  },
  selectedTab: {
    borderBottomWidth: 2,
    borderBottomColor: colors.button.background.primary,
  },
  ///////////////////////////////////////////////
  mPaddingHorizontal: {
    paddingHorizontal: spacing.m,
  },
  mPaddingTop: { paddingTop: spacing.m },
  mRightSpacing: { marginRight: spacing.m },
  mBottomSpacing: { marginBottom: spacing.m },
  lBottomSpacing: {
    marginBottom: spacing.l,
  },
  xxsBottomSpacing: {
    marginBottom: spacing.xxs,
  },
  textAlignRight: { textAlign: 'right' },
  ///////////////////////////////////////////////
  sectionContainer: {
    paddingTop: spacing.l,
    marginBottom: spacing.m,
    marginHorizontal: spacing.s,
    borderTopWidth: 1,
    borderTopColor: colors.border.primary,
  },
  employeeInfo: {
    borderWidth: 1,
    borderColor: colors.border.primary,
    marginBottom: spacing.m,
  },
  employeeStatus: {
    marginBottom: 0,
    padding: spacing.m,
    borderTopWidth: 1,
    borderTopColor: colors.border.primary,
  },
  formLabel: {
    opacity: 0.8,
  },
  mapsButton: {
    borderWidth: 1,
    borderColor: colors.border.primary,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: '65%',
    width: '30%',
  },
  labelMapsButton: {
    color: colors.text.blue,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  switchText: {
    marginRight: spacing.s,
  },
  buttonWidth: {
    width: 30,
  },
  headerContainer: {
    backgroundColor: colors.background.white,
    borderColor: colors.border.primary,
    padding: 16,
    borderWidth: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
  },
  headerText: { flexDirection: 'row', alignItems: 'center' },
  bottomContainer: {
    marginTop: 24,
    padding: 24,
    width: 529,
    backgroundColor: colors.background.white,
  },
  confirmationModalButton: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    paddingVertical: spacing.m,
    paddingRight: spacing.l,
  },
  unverifCategoryContainer: {
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
});
